import {
  JOURNEY_STATUS,
  JOURNEY_STEP,
  MEGASCREENER_DEFAULT_SCREENER_TYPE,
  SCREENER_STATUS,
} from '../../constants';
import { MegaScreenerOutcomeParameter } from '../../features/megaScreenerSlice';
import { RootState } from '../../store/store';
import { post, put } from '../../lib/utils/axiosFetcher';
import { post as cdpPost } from '../../lib/utils/axiosCDPFetcher';
import { logger } from '../../logger';

export type MegaScreenerSitecoreConfig = {
  screenerId: string;
  screenerType: string;
  tenantKey: string;
  isMultiTA: boolean;
  cohortAdditionalParameters: string;
};

export type MegaScreenerPrescreenerContext = {
  potentialParticipant_ID: string;
  potentialId: number;
  screenerId: string;
  screenerType: string;
  tenantKey: string;
  enrollmentId: string;
  guestType: string;
  patientReferralStatus?: string;
  isMultiTARouting: boolean;
  cohortAdditionalParameters: string;
  latestScreenerResponse: any[];
  allScreenerResponse: any[];
};

export function buildMegaScreenerPrescreenerContext(
  state: RootState,
  sitecore: MegaScreenerSitecoreConfig
): MegaScreenerPrescreenerContext | undefined {
  const { potentialParticipant_ID, potentialId, enrollmentId } =
    state.potentialParticipantDetails ?? {};
  const potentialIdNum = typeof potentialId === 'number' ? potentialId : Number(potentialId);

  return {
    potentialParticipant_ID,
    potentialId: potentialIdNum,
    screenerId: sitecore.screenerId,
    screenerType: sitecore.screenerType || MEGASCREENER_DEFAULT_SCREENER_TYPE,
    isMultiTARouting: sitecore.isMultiTA ?? false,
    tenantKey: sitecore.tenantKey || '',
    enrollmentId,
    guestType: state.cdpData?.guestType || 'visitor',
    patientReferralStatus: state.cdpData?.patientReferralStatus,
    cohortAdditionalParameters: sitecore.cohortAdditionalParameters || '',
    allScreenerResponse: state.megaScreener?.allScreenerResponse ?? [],
    latestScreenerResponse: state.megaScreener?.lastScreenerResponse ?? [],
  };
}

const MAX_RETRY_COUNT = Number(process.env.RETRY_COUNT ?? 4);

function getOutcomeValue(params: MegaScreenerOutcomeParameter[], key: string): string | undefined {
  return params.find((param) => param.key === key)?.value;
}

function transformMegaScreenerDataToSaveOutput(
  context: MegaScreenerPrescreenerContext,
  screenerResponseID: string,
  screenerOutcomeParameter: MegaScreenerOutcomeParameter[]
) {
  const pSec = getOutcomeValue(screenerOutcomeParameter, 'pSec');

  return {
    preScreenerMain: {
      potentialId: context.potentialId,
      potentialParticipant_ID: context.potentialParticipant_ID,
      screenerID: context.screenerId,
      screenerResponseID,
      screenerType: context.screenerType || MEGASCREENER_DEFAULT_SCREENER_TYPE,
      screenerStatus: pSec ? SCREENER_STATUS.QUALIFIED : SCREENER_STATUS.DISQUALIFIED,
      screenerCompleted: true,
      base64EncodedSGResponse: null,
      tenantKey: context.tenantKey,
      enrollmentId: context.enrollmentId,
      allScreenerResponse: context.allScreenerResponse
        ? JSON.stringify(context.allScreenerResponse)
        : '',
      latestScreenerResponse: context.latestScreenerResponse
        ? JSON.stringify(context.latestScreenerResponse)
        : '',
      isMultiTARouting: context.isMultiTARouting,
    },
    screenerOutcomeParameter: screenerOutcomeParameter,
    tenantKey: context.tenantKey,
    enrollmentId: context.enrollmentId,
    cohortAdditionalParameters: context.cohortAdditionalParameters,
  };
}

async function postPrescreenerToCdp(
  context: MegaScreenerPrescreenerContext,
  cdpBody: Record<string, unknown>
) {
  let retryAttempt = 0;
  while (retryAttempt < MAX_RETRY_COUNT) {
    retryAttempt += 1;
    const URL = `/api/contacts/AddPrescreenerToDataExtension/${context.potentialParticipant_ID}?guestType=${context.guestType}`;
    try {
      const response = await cdpPost(URL, cdpBody);
      if (response?.status === 200) {
        return;
      }
      if (retryAttempt >= MAX_RETRY_COUNT) {
        logger.error('[MegaScreener] AddPrescreenerToDataExtension failed: non-200 status', {
          guestRefId: context.potentialParticipant_ID,
        });
      }
    } catch (error) {
      if (retryAttempt >= MAX_RETRY_COUNT) {
        logger.error('[MegaScreener] AddPrescreenerToDataExtension failed', {
          error,
          guestRefId: context.potentialParticipant_ID,
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

async function updateScreenerDetailsInCdpOnStart(
  context: MegaScreenerPrescreenerContext,
  screenerResponseID: string
) {
  await postPrescreenerToCdp(context, {
    participantType: null,
    journeyStep: '',
    screenerId: context.screenerId,
    screenerResponseID,
    patientReferralStatus: context.patientReferralStatus,
    screenerType: context.screenerType || MEGASCREENER_DEFAULT_SCREENER_TYPE,
    isMultiTARouting: context.isMultiTARouting,
    screenerStatus: SCREENER_STATUS.DISQUALIFIED,
    screenerCompleted: 'false',
    screenerOutcomeParams: [] as MegaScreenerOutcomeParameter[],
    JourneyStatus: JOURNEY_STATUS.PARTIAL,
    zipCode: '',
  });
}

async function updateScreenerDetailsInCdpOnComplete(
  context: MegaScreenerPrescreenerContext,
  screenerResponseID: string,
  screenerOutcomeParameter: MegaScreenerOutcomeParameter[]
) {
  const pSec = getOutcomeValue(screenerOutcomeParameter, 'pSec');
  const patientType = getOutcomeValue(screenerOutcomeParameter, 'patientType');
  const zipCode = getOutcomeValue(screenerOutcomeParameter, 'zipCode') ?? '';
  const screenerStatus = pSec ? SCREENER_STATUS.QUALIFIED : SCREENER_STATUS.DISQUALIFIED;

  await postPrescreenerToCdp(context, {
    participantType: patientType ?? null,
    journeyStep: JOURNEY_STEP.SCREENER,
    screenerId: context.screenerId,
    screenerResponseID,
    patientReferralStatus: context.patientReferralStatus,
    screenerType: context.screenerType || MEGASCREENER_DEFAULT_SCREENER_TYPE,
    screenerStatus,
    screenerCompleted: 'true',
    screenerOutcomeParams: screenerOutcomeParameter,
    JourneyStatus: JOURNEY_STATUS.PARTIAL,
    zipCode,
    isMultiTARouting: context.isMultiTARouting,
  });
}

export async function addMegaScreenerPrescreenerMain(
  context: MegaScreenerPrescreenerContext,
  screenerResponseID: string
): Promise<void> {
  const transformedData = {
    preScreenerMain: {
      potentialId: context.potentialId,
      potentialParticipant_ID: context.potentialParticipant_ID,
      screenerID: context.screenerId,
      screenerResponseID: screenerResponseID,
      screenerType: context.screenerType
        ? context.screenerType
        : MEGASCREENER_DEFAULT_SCREENER_TYPE,
      screenerStatus: 'disqualified',
      screenerCompleted: false,
      base64EncodedSGResponse: null,
      tenantKey: context.tenantKey,
      enrollmentId: context.enrollmentId,
      isMultiTARouting: context.isMultiTARouting,
    },
    preScreenerOutput: {
      screenerOutput: {},
    },
    tenantKey: context.tenantKey,
    enrollmentId: context.enrollmentId,
  };

  let retryAttempt = 0;
  while (retryAttempt < MAX_RETRY_COUNT) {
    retryAttempt += 1;
    try {
      const { data: response } = await post('/Prescreener/Add', transformedData);
      if (response) {
        await updateScreenerDetailsInCdpOnStart(context, screenerResponseID);
        return;
      }
    } catch (error) {
      if (retryAttempt >= MAX_RETRY_COUNT) {
        logger.error('[MegaScreener] Prescreener/Add failed', {
          error,
          screenerResponseID,
          potentialParticipant_ID: context.potentialParticipant_ID,
        });
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

export async function updateMegaScreenerPrescreenerMain(
  context: MegaScreenerPrescreenerContext,
  screenerResponseID: string,
  screenerOutcomeParameter: MegaScreenerOutcomeParameter[]
): Promise<void> {
  const transformedData = transformMegaScreenerDataToSaveOutput(
    context,
    screenerResponseID,
    screenerOutcomeParameter
  );

  let retryAttempt = 0;
  while (retryAttempt < MAX_RETRY_COUNT) {
    retryAttempt += 1;
    try {
      const { data: response } = await put('/Prescreener/Update', transformedData);
      if (response) {
        await updateScreenerDetailsInCdpOnComplete(
          context,
          screenerResponseID,
          screenerOutcomeParameter
        );
        return;
      }
    } catch (error) {
      if (retryAttempt >= MAX_RETRY_COUNT) {
        logger.error('[MegaScreener] Prescreener/Update failed', {
          error,
          screenerResponseID,
          potentialParticipant_ID: context.potentialParticipant_ID,
        });
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}
